import { useState } from "react";
import { FormData } from "./FormData.tsx";
import {
  FaStar,
  FaParking,
  FaRegBuilding,
  FaRegCalendarAlt,
  FaUsers,
  FaDollarSign,
  FaChargingStation,
  FaTruck,
  FaFireExtinguisher,
  FaChair,
  FaDesktop,
} from "react-icons/fa";
import { MdOutlineMeetingRoom } from "react-icons/md";
import { GiCrane } from "react-icons/gi";
import { TbRulerMeasure } from "react-icons/tb";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import { BsArrowRight } from "react-icons/bs";
import workspaceTypes from "../../../assets/WorkspaceTypes.tsx";
import DOMPurify from "dompurify";
import amenities from "../../../assets/Amenities.tsx";

interface WorkspaceSummaryProps {
  formData: FormData;
}

export default function WorkspaceSummary({ formData }: WorkspaceSummaryProps) {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  const getWorkspaceTypeName = (value: number) => {
    return (
      workspaceTypes.find((type) => type.value === value)?.title ||
      "Not specified"
    );
  };

  const getWorkspaceAmenities = () => {
    if (!formData.workspaceAmenities?.length) return [];
    return formData.workspaceAmenities
      .map((amenityId) => amenities.find((a) => a.value === amenityId))
      .filter((amenity): amenity is NonNullable<typeof amenity> =>
        Boolean(amenity),
      );
  };

  const IconComponent =
    workspaceTypes.find((type) => type.value === formData.workspaceTypeId)
      ?.icon || null;

  const SafeHTML = ({ html }: { html: string }) => (
    <div
      className="prose prose-sm max-w-none"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );

  const workspaceAmenities = getWorkspaceAmenities();
  const hasImages = formData.images?.length > 0;

  const formatCurrency = (val?: number) =>
    val
      ? new Intl.NumberFormat("en-AU", {
          style: "currency",
          currency: "AUD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(val)
      : null;

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Review Your Workspace
          </h1>
          <p className="text-lg text-gray-600">
            Preview how your listing will appear to potential tenants
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            {hasImages && (
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                {/* Main Image */}
                <div className="relative aspect-[16/9] bg-gray-100">
                  <img
                    src={formData.images[selectedImage]?.imageUrl}
                    alt="Workspace"
                    className="w-full h-full object-cover"
                  />
                  {formData.images.length > 1 && (
                    <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1.5 rounded-full text-sm">
                      {selectedImage + 1} / {formData.images.length}
                    </div>
                  )}
                </div>

                {/* Thumbnails */}
                {formData.images.length > 1 && (
                  <div className="p-4 flex gap-2 overflow-x-auto">
                    {formData.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all duration-200 ${
                          selectedImage === index
                            ? "ring-2 ring-blue-500 ring-offset-2"
                            : "opacity-70 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={image.imageUrl}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Title & Basic Info */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    {formData.title || "Untitled Workspace"}
                  </h2>
                  <div className="flex items-center gap-4 text-gray-600">
                    <div className="flex items-center gap-1">
                      <FaStar className="text-yellow-400" />
                      <span className="font-medium">New Listing</span>
                    </div>
                    <span>•</span>
                    <span>No reviews yet</span>
                  </div>
                </div>
                {IconComponent && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                    <IconComponent className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-700">
                      {getWorkspaceTypeName(formData.workspaceTypeId || 0)}
                    </span>
                  </div>
                )}
              </div>

              {/* Address */}
              <div className="flex items-start gap-3 text-gray-600 bg-gray-50 rounded-xl p-4">
                <HiOutlineOfficeBuilding className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">
                  {formData.address || "Address"}, {formData.city || "City"},{" "}
                  {formData.state || "State"} {formData.postalCode || ""}
                </p>
              </div>

              {/* Key Specs */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <FaUsers className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-xs text-gray-500">Capacity</p>
                    <p className="font-semibold">
                      {formData.capacity || "N/A"} people
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <TbRulerMeasure className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-xs text-gray-500">Size</p>
                    <p className="font-semibold">
                      {formData.sizeSqm || "N/A"} m²
                    </p>
                  </div>
                </div>
                {formData.isFurnished && (
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                    <FaChair className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-xs text-green-600">Furnished</p>
                      <p className="font-semibold text-green-700">
                        Ready to move
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                About This Space
              </h3>
              <div className="relative">
                <div className={!showFullDescription ? "line-clamp-3" : ""}>
                  <SafeHTML
                    html={formData.description || "No description provided"}
                  />
                </div>
                {formData.description?.length > 200 && (
                  <button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="mt-4 text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                  >
                    {showFullDescription ? "Show less" : "Read more"}
                    <BsArrowRight
                      className={`w-4 h-4 transition-transform ${showFullDescription ? "rotate-90" : ""}`}
                    />
                  </button>
                )}
              </div>
            </div>

            {/* Amenities */}
            {workspaceAmenities.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  Amenities ({workspaceAmenities.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {workspaceAmenities
                    .slice(0, showAllAmenities ? undefined : 9)
                    .map((amenity) => (
                      <div
                        key={amenity.value}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <span className="text-blue-600">
                          {amenity.icon ? (
                            <amenity.icon className="w-5 h-5" />
                          ) : (
                            <span className="w-5 h-5" />
                          )}
                        </span>
                        <span className="text-gray-700">{amenity.title}</span>
                      </div>
                    ))}
                </div>
                {workspaceAmenities.length > 9 && (
                  <button
                    onClick={() => setShowAllAmenities(!showAllAmenities)}
                    className="mt-6 text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                  >
                    {showAllAmenities
                      ? "Show less"
                      : `View all ${workspaceAmenities.length} amenities`}
                    <BsArrowRight
                      className={`w-4 h-4 transition-transform ${showAllAmenities ? "rotate-90" : ""}`}
                    />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Property Details */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Listing Summary
              </h3>

              {/* Property Details Sections */}
              {(() => {
                const sections = [
                  {
                    title: "Space Details",
                    condition:
                      formData.rooms || formData.meetingRooms || formData.desks,
                    items: [
                      {
                        label: "Rooms",
                        value: formData.rooms,
                        icon: MdOutlineMeetingRoom,
                      },
                      {
                        label: "Meeting Rooms",
                        value: formData.meetingRooms,
                        icon: MdOutlineMeetingRoom,
                      },
                      {
                        label: "Desks",
                        value: formData.desks,
                        icon: FaDesktop,
                      },
                    ],
                  },
                  {
                    title: "Building",
                    condition:
                      formData.buildingClass ||
                      formData.yearBuilt ||
                      formData.renovationYear,
                    items: [
                      {
                        label: "Class",
                        value: formData.buildingClass,
                        icon: FaRegBuilding,
                      },
                      {
                        label: "Year Built",
                        value: formData.yearBuilt,
                        icon: FaRegCalendarAlt,
                      },
                      {
                        label: "Renovated",
                        value: formData.renovationYear,
                        icon: FaRegCalendarAlt,
                      },
                    ],
                  },
                  {
                    title: "Industrial Features",
                    condition:
                      formData.loadingDockCount ||
                      formData.overheadCranes ||
                      formData.sprinklerSystem,
                    items: [
                      {
                        label: "Loading Docks",
                        value: formData.loadingDockCount,
                        icon: FaTruck,
                      },
                      {
                        label: "Overhead Cranes",
                        value: formData.overheadCranes ? "Available" : null,
                        icon: GiCrane,
                      },
                      {
                        label: "Sprinkler System",
                        value: formData.sprinklerSystem ? "Installed" : null,
                        icon: FaFireExtinguisher,
                      },
                    ],
                  },
                  {
                    title: "Parking",
                    condition:
                      formData.parkingSpotsTotal ||
                      formData.electricVehicleCharging,
                    items: [
                      {
                        label: "Total Spots",
                        value: formData.parkingSpotsTotal,
                        icon: FaParking,
                      },
                      {
                        label: "EV Charging",
                        value: formData.electricVehicleCharging,
                        icon: FaChargingStation,
                      },
                      {
                        label: "Parking Fee",
                        value: formatCurrency(formData.parkingFee),
                        icon: FaDollarSign,
                      },
                    ],
                  },
                ];

                return sections.map((section) => {
                  if (!section.condition) return null;

                  const validItems = section.items.filter((item) => item.value);
                  if (validItems.length === 0) return null;

                  return (
                    <div key={section.title} className="mb-6 last:mb-0">
                      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                        {section.title}
                      </h4>
                      <div className="space-y-3">
                        {validItems.map((item) => (
                          <div
                            key={item.label}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2 text-gray-600">
                              {item.icon && <item.icon className="w-4 h-4" />}
                              <span className="text-sm">{item.label}</span>
                            </div>
                            <span className="font-medium text-gray-900">
                              {item.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
